import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import { Button, Loading, Page } from '@fielded/shared-ui'

import { hasFeature } from '../van-shared/utils/features'
import { userIsAuthorised } from '../van-shared/utils/auth'
import { selectHasReceivedMasterData } from '../root/reducers/analytics/master-data'
import {
  hasAnalyticsAccess,
  isPsmFacilityUser,
  isPsmResellerApp
} from '../van-shared/utils/utils'

import CrossLink from '../common/cross-link'
import HomeNotificationView from '../van-shared/components/HomeNotificationView/HomeNotificationView'
import FacilityDeliveries from '../subapps/shipments/deliveries/psm-deliveries/PSMFacilityDeliveries/FacilityDeliveries'

const Home = ({
  user,
  showProfileLink,
  api,
  config,
  history
}) => {
  const [shipments, setShipments] = useState()
  const [loading, setLoading] = useState(true)
  const [notificationOptions, setNotificationOptions] = useState({
    notificationsList: [],
    showNotificationsBanner: false,
    notificationsMarkedAsRead: false
  })

  const isPsmReseller = isPsmResellerApp(config)
  const isPsmFacility = isPsmFacilityUser(user)
  const showPsmFacilityView = hasFeature(config, 'features.showFacilityView')
  const hasShipments = hasFeature(config, 'features.shipments') && userIsAuthorised(user, 'feature:shipments')
  const hasPsmFacilityView = isPsmFacility && showPsmFacilityView && hasShipments

  const hasAnalytics = hasAnalyticsAccess(user, config)
  const canFetchAnalyticsMasterData = hasAnalytics && isPsmReseller
  const hasAnalyticsMasterData = canFetchAnalyticsMasterData ? useSelector(selectHasReceivedMasterData) : null

  const { notificationsList, notificationsMarkedAsRead, showNotificationsBanner } = notificationOptions

  const markNotificationsRead = async () => {
    // check if there are any unread notifications
    const unreadListLength = notificationsList && notificationsList.filter(notification => !notification.readAt).length

    let updatedNotificationsList = notificationsList
    if (unreadListLength) {
      try {
        await api.notification.markAsRead({})
      } catch (e) {
        console.log(e)
      }
      updatedNotificationsList = await api.notification.getList({ limit: 4, offset: 0 })
    }

    setNotificationOptions(prevValues => ({
      ...prevValues,
      notificationsMarkedAsRead: true,
      notificationsList: updatedNotificationsList
    }))
  }

  const toggleNotificationsBanner = () => {
    if (showNotificationsBanner && !notificationsMarkedAsRead) {
      markNotificationsRead()
    }
    setNotificationOptions(prevValues => ({
      ...prevValues,
      showNotificationsBanner: !showNotificationsBanner
    }))
  }

  const initialize = async () => {
    // On initial login, redirect PSM resellers to the Analytics page
    // if master data hasn't been received yet
    if (canFetchAnalyticsMasterData && !hasAnalyticsMasterData) {
      return history.push('/analytics')
    }

    try {
      if (hasPsmFacilityView) {
        const shipments = await api.shipment.find({ loaction: user.location })
        setShipments(shipments)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    initialize()
  }, [])

  if (loading) {
    return <Loading />
  }

  return (
    <div className='home__container'>
      <Page>
        {hasPsmFacilityView ? (
          <Page.Panel className='home__container-notification' narrow alignLeft relative>
            <HomeNotificationView
              username={user.name}
              currentUser={user}
              config={config}
              api={api}
              onToggleNotificationsBanner={toggleNotificationsBanner}
              cardVisible={showNotificationsBanner}
              notifications={notificationsList}
            />
          </Page.Panel>
        ) : (
          <div>
            {user.name && (
              <div>
                You are logged in as {user.name}.
              </div>
            )}
          </div>
        )}

        {showProfileLink && !hasPsmFacilityView && (
          <Page.Panel.Message
            className='user-require-fill-email'
            type='info'
            fullWidth
            cta={
              <Button
                colorVariant='brand'
                component={CrossLink}
                to={'/profile'}
                fill='full'
              >
                Review Profile
              </Button>
            }
          >
            Hello <strong>{user.name}</strong>, please capture your profile details.
            This is important for password recovery.
          </Page.Panel.Message>
        )}

        <section className='home__content-wrapper'>
          {hasPsmFacilityView && (
            <FacilityDeliveries
              user={user}
              history={history}
              shipments={shipments}
              showCompleted={false}
            />
          )}
        </section>
      </Page>
    </div>
  )
}

Home.propTypes = {
  api: PropTypes.object,
  user: PropTypes.object,
  history: PropTypes.object,
  config: PropTypes.object,
  showProfileLink: PropTypes.bool
}

Home.defaultProps = {
  user: {}
}

export default Home
